"use client";

import { useTranslations } from "next-intl";
import { useState } from "react";

import {
	Dialog,
	DialogContent,
	type DialogProps,
	DialogTitle,
	IconButton,
	Typography,
	useMediaQuery,
	useTheme,
} from "@/components/controls/base-controls";
import { MdClose } from "@/components/controls/icons";
import MfaPreferenceForm from "@/components/forms/mfa-preference.form";
import type { MfaType } from "@/core/types/mfa.type";

const MfaDialog = ({
	mfaPreference,
	open,
	onClose,
	onMfaSubmit,
	scroll = "paper",
}: {
	mfaPreference: MfaType | undefined,
	open: boolean;
	onClose: () => void;
	onMfaSubmit: (mfa: MfaType) => void;
	scroll: DialogProps["scroll"];
}) => {
	const t = useTranslations();

	const theme = useTheme();
	// Returns true if screen width is 'sm' (default 600px) or smaller
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const [preferredMfa] = useState<MfaType | undefined>(mfaPreference);

	const handleClose = async () => {
		onClose();
	};

	const handleSubmit = (data: MfaType) => {
		// Selection-only: emit the chosen MFA type and let the calling page own
		// the setSignInMfaPreference call and next-step routing. This avoids the
		// preference being set twice (once here, once in the parent handler).
		if (data && data !== preferredMfa) {
			onMfaSubmit(data);
		}
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullScreen={isMobile}
			scroll={scroll}
			disableRestoreFocus={true}
			aria-labelledby="dialog-title"
			aria-describedby="dialog-description"
			slotProps={{
				paper: {
					sx: {
						[theme.breakpoints.up("md")]: {
							height: "14.5em",
							maxHeight: "40vh",
							width: "24em",
							maxWidth: "60vw",
						},
					},
				},
			}}
		>
			<DialogTitle
				sx={{
					backgroundColor: "var(--mui-palette-dialog-title-bg)",
					display: "flex",
					flexDirection: "row",
					justifyContent: "space-between",
					alignItems: "center",
					margin: "0 auto",
					padding: ".6em .6em .6em .8em",
					width: "100%",
				}}
			>
				<Typography
					variant="body1"
					sx={{
						color: "var(--mui-palette-lightest)",
						mt: { xs: "4px", md: "3px" },
					}}
				>
					{t("mfa.select")}
				</Typography>
				<IconButton
					onClick={onClose}
					aria-label="close"
					sx={{ color: "var(--mui-palette-lightest)" }}
				>
					<MdClose />
				</IconButton>
			</DialogTitle>

			<DialogContent
				dividers
				sx={{
					backgroundColor: "var(--mui-palette-page-bg)",
					padding: "1em",
					pb: { md: "2.5em" }, // Adds more bottom padding for desktop view
					display: "flex",
					flexDirection: "column",
				}}
			>
				<MfaPreferenceForm
					preferredMfa={preferredMfa}
					onMfaSubmit={(value: MfaType) => {
						handleSubmit(value);
					}}
				/>
			</DialogContent>
		</Dialog>
	);
};

export default MfaDialog;
